<!-- eslint-disable max-len -->
<template>
  <div :class="$options.name">
    <v-card elevation="0">
      <v-card-text>
        <v-switch
          v-model="geoServerType"
          label="Подключение к геосерверу МЧС"
        />
        <template v-if="!geoServerType">
          <small>
            На карту будут выводится тайлы из предоставленных ссылок
          </small>
          <div v-for="(tile, key) in settingsValue.tiles" :key="key" class="d-flex align-center">
            <v-text-field
              v-model="settingsValue.tiles[key]"
              :label="`Ссылка на тайл №${key + 1}`"
            />
            <v-btn color="error" class="ml-4" @click="removeTile(key)">
              Удалить
            </v-btn>
          </div>
          <div class="d-flex justify-end my-4">
            <v-btn color="success" @click="addTile">Добавить</v-btn>
          </div>
        </template>
        <template v-else>
          <small>
            Подгрузка слоев через геосервер МЧС
          </small>
          <div>
            <v-text-field
              v-model="settingsValue.geoserverUrl"
              label="Путь до геосервера"
              :disabled="layers.length > 0"
              hide-details
            />
            <small>Любой запрос, как пример. Для получения данных посредством `GetFeatureInfo`, укажите путь-пример именно такого запроса.</small>
            <small class="d-block">
              Пример:
              <span class="green--text">https://mygeoserver.ru/geoservers/newgeoserver1/wms?service=wms&version=1.1.1</span><span class="orange--text">&request=GetFeatureInfo</span>
            </small>
            <small>Если слой планируется публиковать в открытой части, то желательно использовать домен <span class="green--text">tiles.atlas.mchs.ru</span>, добавив в конце адреса параметр <span class="orange--text">&layers=</span>(для wms) или <span class="orange--text">&typeName=</span>(для wfs) с системным наименованием слоя.</small>
            <small class="d-block">
              <div>(!)Но, так как <span class="green--text">tiles.atlas.mchs.ru</span> работает по белому списку слоёв, предварительно следует направить системное наименования необходимых слоёв в ИАЦ.</div>
            </small>

            <v-btn
              v-if="layers.length > 0"
              class="d-block my-1"
              x-small
              @click="layers = []"
            >
              Сменить сервер
            </v-btn>

            <div v-if="layers.length">
              <v-autocomplete
                v-model="settingsValue.layer"
                :items="layers"
                no-data-text="нет данных"
                item-value="value"
                item-text="text"
                label="Слой"
                placeholder="Поиск слоя"
                clearable
              />
            </div>
          </div>
          <div v-if="layers.length === 0" class="d-flex justify-end">
            <v-btn
              class="primary"
              :disabled="!settingsValue.geoserverUrl"
              @click="getGeoserverInfo"
            >
              Далее
            </v-btn>
          </div>
        </template>

        <br>
        <v-divider />
        <br>

        <div class="d-flex align-center">
          <v-checkbox
            v-model="settingsValue.timelineType"
            label="Привязка к таймлайну"
          />
        </div>

        <v-select
          v-model="settingsValue.scheme"
          :items="SCHEME_ITEMS"
          label="Схема"
        />
        <v-slider
          v-model="settingsValue.opacity"
          prepend-icon="visibility"
          label="Прозрачность"
          thumb-label
          step="0.1"
          max="1"
          min="0"
        />

        <template v-if="geoServerType">
          <br>
          <v-divider />
          <br>

          <div class="d-flex align-center">
            <h3>Список полей</h3>
            <v-btn class="ml-1" x-small icon @click="addMapping">
              <v-icon>add</v-icon>
            </v-btn>
          </div>
          <small>
            Данная функция предназначена для переименования
            и исключения ключей из карточки геометрии
          </small>
          <div
            v-for="(mapping, key) in settingsValue.mappings"
            :key="key"
            class="d-flex align-center"
          >
            <v-text-field
              v-model="settingsValue.mappings[key].key"
              class="mr-2"
              label="Ключ"
              hide-details
            />
            <v-text-field
              v-model="settingsValue.mappings[key].value"
              label="Значение"
              hide-details
            />
            <v-checkbox
              v-model="settingsValue.mappings[key].except"
              class="ml-2"
              label="Исключить"
              hide-details
            />
            <v-btn class="ml-2 mt-4" icon color="red" @click="removeMapping(key)">
              <v-icon>delete</v-icon>
            </v-btn>
          </div>
          <div v-if="settingsValue.mappings.length === 0">
            Необходимо заполнить список полей
          </div>
        </template>

        <br>
        <v-divider />
        <br>

        <div class="d-flex align-center">
          <h3>Легенда</h3>
          <v-btn icon x-small class="ml-1" @click="addLegend()">
            <v-icon>add</v-icon>
          </v-btn>
        </div>
        <small>
          Данная функция предназначена для описания цветов используемых слоем
        </small>
        <div>
          <div
            v-for="(legend, key) in settingsValue.legend"
            :key="key"
            class="d-flex align-center"
          >
            <v-menu
              :close-on-content-click="false"
              offset-x
            >
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  :color="settingsValue.legend[key].color.hex"
                  :class="[`${getTextColor(settingsValue.legend[key].color.hex)}--text`]"
                  class="mr-4"
                  small
                  rounded
                  dark
                  v-bind="attrs"
                  v-on="on"
                >
                  Выбрать цвет
                </v-btn>
              </template>

              <v-card>
                <v-color-picker
                  v-model="settingsValue.legend[key].color"
                  style="flex: 1"
                  flat
                  hide-alpha-controls
                />
              </v-card>
            </v-menu>

            <v-text-field
              v-model="settingsValue.legend[key].text"
              label="Название"
            />

            <v-btn class="ml-1"
                   color="error"
                   icon
                   small
                   @click="removeLegend(key)"
            >
              <v-icon>delete</v-icon>
            </v-btn>
          </div>
          <div v-if="settingsValue.legend.length === 0">
            Не добавлено ни одной легенды
          </div>
        </div>
      </v-card-text>
    </v-card>

    <div class="mt-2 d-flex pa-5">
      <div class="d-flex flex-grow-1" />
      <v-btn color="primary" @click="onSave">
        Сохранить
      </v-btn>
    </div>
  </div>
</template>

<script>
/* eslint-disable max-len */
import http from '@/utils/api';
import GetTextColor from '@/utils/get-text-color';

const createDefaultLegend = () => ({
  text: '',
  color: {
    hex: '#FF0000',
  },
});

const createDefaultMapping = () => ({
  key: '',
  value: '',
  except: false,
});

export default {
  name: 'tms-tab',
  model: {
    prop: 'value',
    event: 'input',
  },
  props: {
    value: {
      type: [Object, null],
      default: () => {},
    },
  },
  data() {
    return ({
      settingsValue: {
        tiles: [''],
        scheme: 'tms',
        opacity: 1.0,
        legend: [],
        mappings: [],
        geoserverUrl: '',
        layer: '',
        type: 'geoserver',
      },
      SCHEME_ITEMS: [
        { value: 'tms', text: 'TMS' },
        { value: 'xyz', text: 'XYZ' },
      ],
      geoServerType: this.value.type === 'geoserver',
      layers: [],
    });
  },
  computed: {
    getTextColor() {
      return (i) => GetTextColor(i);
    },
  },
  async created() {
    this.settingsValue = {
      ...this.settingsValue,
      ...this.value,
    };
    if (this.settingsValue.geoserverUrl) {
      await this.getGeoserverInfo();
    }
    this.valuesWithoutChange = Object.assign({}, this.settingsValue);
    this.$emit('registerGuard', [this.valuesWithoutChange, this.beforeLeave]);
  },
  methods: {
    beforeLeave() {
      return this.settingsValue;
    },
    addTile() {
      this.settingsValue.tiles.push('');
    },

    removeTile(key) {
      this.settingsValue.tiles.splice(key, 1);
    },

    parseStr(str) {
      // eslint-disable-next-line prefer-destructuring
      return Array.from((new URLSearchParams(str)).entries()).reduce((acc, i) => { acc[i[0]] = i[1]; return acc; }, {});
    },

    lowerCaseObject(obj) {
      return Object.keys(obj).reduce(
        (acc, k) => {
          acc[k.toLowerCase()] = obj[k];
          return acc;
        }, {},
      );
    },

    httpBuildQuery(obj) {
      const urlParams = new URLSearchParams();
      Object.keys(obj).map(k => urlParams.append(k, obj[k]));
      return urlParams.toString().replace(/%7B/g, '{').replace(/%7D/g, '}'); // restore our variables format
    },

    getCombinedUrl(baseUrl, postfixPath, systemArgs, optionalArgs = '') {
      // prepare source data
      const baseUrlElements = new URL(baseUrl); // parse_url
      // eslint-disable-next-line no-param-reassign
      postfixPath = postfixPath.replace(/^\/*/, '').replace(/\/*$/, ''); // trim

      // parse_str()
      const systemArgsArr = this.parseStr(systemArgs);
      const optionalArgsArr = this.parseStr(optionalArgs);

      // fix handler-script
      let resultPath = '';
      if (baseUrlElements.pathname) {
        resultPath = baseUrlElements.pathname.replace(/\/*$/, ''); // rtrim
        // fix handler-script
        if (resultPath.match(/\/(wms|wfs|wcs|wmts|wps|wcps)$/i)) { // replace wrong standard
          resultPath = resultPath.substr(0, resultPath.length - RegExp.$1.length) + postfixPath;
        } else if (resultPath.match(/\.[a-zA-Z0-9]{2,4}$/)) {
          // already has specific (script.php, script.aspx,..)
        } else {
          resultPath += `/${postfixPath}`;
        }
      } else {
        resultPath = `/${postfixPath}`;
      }

      // combine args
      let resultArgs = {};
      let sourceArgsArr = {};
      if (baseUrlElements.search !== '') {
        sourceArgsArr = this.parseStr(baseUrlElements.search);
        const systemArgsArrLC = this.lowerCaseObject(systemArgsArr);
        Object.keys(sourceArgsArr).forEach((k) => {
          const lk = k.toLowerCase();
          if (!systemArgsArrLC[lk]) { // filter system arguments
            resultArgs[k] = sourceArgsArr[k];
          }
        });
      }
      resultArgs = { ...optionalArgsArr, ...resultArgs, ...systemArgsArr };
      // clear undefined values (not required, but defined as system to clear)
      resultArgs = Object.keys(resultArgs).reduce((acc, k) => {
        if (k.toLowerCase() === 'styles' || resultArgs[k] !== '') { // `styles` can has empty value
          acc[k] = resultArgs[k];
        }
        return acc;
      }, {});

      // collect result
      if (!baseUrlElements.protocol) {
        baseUrlElements.protocol = 'https:';
      }
      if (!baseUrlElements.hostname) {
        baseUrlElements.hostname = '';
      }
      let resultUrl = `${baseUrlElements.protocol}//${baseUrlElements.hostname}`;
      if (baseUrlElements.port !== '') {
        resultUrl += `:${baseUrlElements.port}`;
      }
      resultUrl += resultPath;
      resultUrl += `?${this.httpBuildQuery(resultArgs)}`;

      return resultUrl;
    },

    onSave() {
      this.settingsValue.type = this.geoServerType ? 'geoserver' : 'urls';
      if (this.geoServerType) {
        // prepare tiles url based on geoserver url
        const tilesUrl = this.getCombinedUrl(
          this.settingsValue.geoserverUrl,
          'wms',
          `service=WMS&request=GetMap&layers=${this.settingsValue.layer}&bbox={bbox-epsg-3857}&srs=EPSG%3A3857&styles=&format=image/png&info_format=`,
          'width=256&height=256',
        );

        // prepare tiles url
        this.settingsValue.tiles = [tilesUrl];
      }
      this.valuesWithoutChange = Object.assign({}, this.settingsValue);
      this.$emit('registerGuard', [this.valuesWithoutChange, this.beforeLeave]);
      this.$emit('save', this.settingsValue);
    },

    addMapping() {
      this.settingsValue.mappings.push(createDefaultMapping());
    },

    removeMapping(key) {
      this.settingsValue.mappings.splice(key, 1);
    },

    addLegend() {
      this.settingsValue.legend.push(createDefaultLegend());
    },

    removeLegend(key) {
      this.settingsValue.legend.splice(key, 1);
    },

    async getGeoserverInfo() {
      const layersRawList = await http.get(
        'admin/wms/info',
        {
          params: {
            geoserver: this.settingsValue.geoserverUrl,
          },
        },
      );

      let layersList = [];
      if (typeof layersRawList === 'object' && !layersRawList.pop && layersRawList != null) { // isObject
        // https://vuetifyjs.com/en/api/v-select/#props
        Object.keys(layersRawList).forEach((k) => {
          if (typeof layersRawList[k] !== 'string') {
            return;
          }
          layersList.push({
            text: `${layersRawList[k]} (${k})`, // sometimes we have duplicate names for different keys
            value: k,
          });
        });
      } else {
        layersList = layersRawList;
      }

      this.layers = layersList;

      const layer = this.layers.find(({ value }) => value?.toLowerCase() === this.settingsValue.layer?.toLowerCase());
      if (layer?.value) this.settingsValue.layer = layer.value;
    },
  },
};
</script>
